import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import _ from 'lodash';
import { useConfig } from '@lore/config';
import { useUser } from '@lore/auth';
import queryString from 'query-string';
import validators from '@/utils/validators';
import Overlay from '@/forms/_common/Overlay';
import FormErrors from '@/forms/_common/FormErrors';
import RequestError from '@/forms/_common/RequestError';
import ErrorMessage from '@/forms/_common/ErrorMessage';
import DialogTemplate from '@/dialogs_common/DialogTemplate';
import FormTemplate from '@/dialogs_common/FormTemplate';
import FormField from '@/forms/_common/FormField';
import StringField from '@/forms/_fields_v2/StringField';
import TextAreaField from '@/forms/_fields_v2/TextAreaField';
import SolidButton from '@/components/Button/SolidButton';
import { PayloadStates } from '@lore/utils';
import { OutlineButton } from '@/components/Button';
import FormTemplateButtonFooter from '@/dialogs_common/FormTemplateButtonFooter';
import usePotentialSites from '@/hooks/usePotentialSites';
/** @typedef {import('@/models/currentUser').LoreModel} LoreModel */
import { useQueryMutation } from '@lore/query-connect';
import {useQueryClient} from '@tanstack/react-query';
import storage, {SCHOOL_DRAFT_KEY} from '@/utils/storage'

const QUESTIONS = [
  {
    key: 'name',
    label: 'School or site name',
    placeholder: 'My school',
    required: true,
    type: 'string'
  },
  {
    key: 'website',
    label: 'School/site website',
    placeholder: 'https://www.example.com/',
    required: false,
    type: 'string'
  },
  {
    key: 'phone',
    label: 'Phone Number',
    placeholder: '555-555-1212',
    required: true,
    type: 'string',
    readOnlyIfValue: true
  },
  {
    key: 'address',
    label: 'Address',
    placeholder: 'Address...',
    required: true,
    type: 'string',
    readOnlyIfValue: true
  },
  {
    key: 'city',
    label: 'City',
    placeholder: 'City...',
    required: true,
    type: 'string',
    readOnlyIfValue: true
  },
  {
    key: 'state',
    label: 'State',
    placeholder: 'State...',
    required: true,
    type: 'string',
    readOnlyIfValue: true
  },
  {
    key: 'zip',
    label: 'ZIP Code',
    placeholder: 'ZIP Code...',
    required: true,
    type: 'string',
    readOnlyIfValue: true
  },
  {
    key: 'num-learners',
    label: 'Number of learners',
    placeholder: '9',
    required: true,
    type: 'string'
  },
  {
    key: 'free-reduced-lunch',
    label: 'Approximately what percentage of students at your school or site are eligible for the Free and Reduced Lunch Program?',
    description: "If you’re unsure an estimate or knowledge you have from other sources of income data is welcome",
    placeholder: '1%',
    required: true,
    type: 'string'
  },
  {
    key: 'what-types-of-experience',
    label: "What types of experience have your educators had with community-engaged learning?",
    description: "This might include professional learning, actual projects",
    placeholder: 'More information...',
    required: false,
    type: 'text'
  },
  {
    key: 'why',
    label: "Why CommunityShare?",
    description: "What benefits do you hope to gain? What challenges do you hope CommunityShare can support?",
    placeholder: 'More information...',
    required: false,
    type: 'text'
  }
];

Info.propTypes = {
  user: PropTypes.shape({
    data: PropTypes.shape({
      email: PropTypes.string.isRequired,
      customTags: PropTypes.string
    }).isRequired
  }).isRequired,
  data: PropTypes.shape({
    potentialSiteId: PropTypes.number,
    more: PropTypes.string
  }).isRequired,
  onNext: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

/**
 * @param {Object} props
 * @param {LoreModel} props.user
 * @param {Object} props.data
 * @param {Function} props.onNext
 * @param {Function} props.onCancel
 * @returns {JSX.Element}
 */
export default function Info(props) {
  const {
    user,
    data: propData,
    onNext,
    onCancel
  } = props;


  const [data, setData] = useState(() => {
    const saved = storage.get(SCHOOL_DRAFT_KEY);
    const initialData = _.pick(propData, _.map(QUESTIONS, 'key'));
    if (saved) {
      return _.merge({}, initialData, JSON.parse(saved));
    }
    return initialData;
  });
  const [showErrors, setShowErrors] = useState(false);
  const [_request, setRequest] = useState(null);
  const [finished, setFinished] = useState(false);

  const request = _request;
  const saving = request && request.state === PayloadStates.CREATING;
  const error = request && request.state === PayloadStates.ERROR_CREATING;
  const success = request && request.state === PayloadStates.RESOLVED;

  const currentUser = useUser();
  const config = useConfig();
  const { update } = usePotentialSites();
  const userMutation = useQueryMutation('currentUser.updateSingleton', currentUser);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (success && !finished) {
      setFinished(true);
      onNext(data);
    }
  });

  function onSubmit() {
    const blocks = [
      {
        type: "paragraph",
        text: `My school is missing.`
      }
    ];
    if (propData.more) {
      blocks.push({
        type: "paragraph",
        text: propData.more
      });
    }

    QUESTIONS.forEach((question) => {
      blocks.push({
        type: "paragraph",
        text: `Q: ${question.label}`
      });
      blocks.push({
        type: "paragraph",
        text: `A: ${data[question.key] || ''}`
      });
    });

    axios({
      url: config.intercom.messengerUrl,
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      data: queryString.stringify({
        app_id: config.intercom.appId,
        user_data: JSON.stringify({
          email: user.data.email
        }),
        blocks: JSON.stringify(blocks)
      })
    }).then(async function () {
      if (propData.potentialSiteId) {
        update(propData.potentialSiteId, {
          reviewStatus: 'pending'
        });
      }

      const currentTags = currentUser.data.customTags ? currentUser.data.customTags.split(',') : [];
      if (!currentTags.includes('_csSiteStarted')) {
        currentTags.push('_csSiteStarted');
        await userMutation.mutateAsync({ customTags: currentTags.join(',') });
        await queryClient.invalidateQueries(['currentUser']);
      }

      storage.delete(SCHOOL_DRAFT_KEY);
      setRequest({
        state: PayloadStates.RESOLVED
      });
    }).catch(function(error) {
      setRequest({
        state: PayloadStates.ERROR_CREATING,
        error: error
      });
    });

    setRequest({
      state: PayloadStates.CREATING
    });
  }

  function onChange(name, value) {
    setData(_.merge({}, data, {
      [name]: value
    }));
  }

  function handleDraft() {
    storage.set(SCHOOL_DRAFT_KEY, JSON.stringify(data));
    return onCancel()
  }

  return (
    <DialogTemplate className="w-screen max-w-lg">
      <Overlay isVisible={saving}>
        <FormErrors data={data} validators={
          QUESTIONS.reduce((acc, question) => {
            if (question.required) {
              acc[question.key] = [validators.isRequired];
            }
            return acc;
          }, {})
        }>
          {({ errors, hasError }) => {
            return (
              <FormTemplate
                title="My school or site"
                subtitle="Let’s get some basic information about you and the school or site you are connected with! We’ll use your responses only to reach out to you about CommunityShare and how to become part of our network."
                onClose={onCancel}
                alert={error ? (
                  <RequestError request={request} />
                ) : showErrors ? (
                  <ErrorMessage>
                    Please fill in all required fields.
                  </ErrorMessage>
                ) : null}
                body={(
                  <div className="space-y-4">
                    {QUESTIONS.map((question) => {
                      const value = propData[question.key];

                      // if we already have the data from the user record or the site/potential site record,
                      // do not ask for it again
                      if (question.readOnlyIfValue && value) {
                        return null
                      }

                      return (
                        <FormField key={question.key} name={question.key} data={data} errors={showErrors && errors} onChange={onChange}>
                          {(field) => {
                            if (question.type === 'text') {
                              return (
                                <TextAreaField
                                  label={question.label}
                                  description={question.description}
                                  placeholder={question.placeholder}
                                  rows={3}
                                  value={field.value || ""}
                                  error={field.error}
                                  onChange={field.onChange}
                                />
                              );
                            }

                            return (
                              <StringField
                                label={question.label}
                                description={question.description}
                                placeholder={question.placeholder}
                                value={field.value || ""}
                                error={field.error}
                                onChange={field.onChange}
                              />
                            );
                          }}
                        </FormField>
                      );
                    })}
                  </div>
                )}
                footer={(
                  <FormTemplateButtonFooter>
                    <OutlineButton onClick={handleDraft}>
                      Save as Draft
                    </OutlineButton>
                    <SolidButton onClick={() => hasError ? setShowErrors(true) : onSubmit()}>
                      Submit
                    </SolidButton>
                  </FormTemplateButtonFooter>
                )}
              />
            );
          }}
        </FormErrors>
      </Overlay>
    </DialogTemplate>
  );
};
